import { Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography, Checkbox } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React from 'react';

import AssociatedTagCategories from '../trips/AssociatedTagCategories';
import CheckinAndBoardingSection from '../trips/CheckinAndBoardingSection';

const useStyles = makeStyles(() => ({
  compactControlLabel: {
    '& .MuiFormControlLabel-label': {
      lineHeight: 1.4,
    },
    '& .MuiCheckbox-root, & .MuiRadio-root': {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
}));

const TripDefaultSettings1 = (props) => {
  const classes = useStyles();
  const {
    mode = 'operator',
    hasWritePerm = false,
    // eslint-disable-next-line no-unused-vars
    hasUpdateTagCategoriesPerm = false, // unused in v1, kept for interface compatibility

    // shared controls
    locationSharingValue,
    onLocationSharingChange,
    myTripsDisplayValue,
    onMyTripsDisplayChange,
    hasGroupChatValue,
    onHasGroupChatChange,
    excludeFromChatValue,
    onExcludeFromChatChange,
    // CP-8278: trip mode only -- the operator level already has this switch in
    // topconf (disable_internal_guides_and_staff_conversations).
    disableInternalChatValue,
    onDisableInternalChatChange,

    // Operator defaults (for merging in trip mode) - unused in v1, kept for interface compatibility
    // eslint-disable-next-line no-unused-vars
    operatorDefaultCategories = [],

    // operator defaults categories
    defaultsAssociatedTagCategories = [],
    onSetDefaultsAssociated,

    // trip mode extras
    trip,
    onTripChange,
    clientErrors,
    isParentTouched,
  } = props;

  const locationSharingScopeLabel = mode === 'trip' ? 'for this tour' : 'for all tours';

  return (
    <>
      <Grid item xs={12} md={6}>
        <Typography fontWeight={500}>Location Sharing</Typography>
        <FormControl fullWidth variant="filled" size="small" disabled={!hasWritePerm}>
          <RadioGroup
            name="defaults_location_sharing"
            value={(locationSharingValue ?? '').toString()}
            onChange={onLocationSharingChange}
          >
            <FormControlLabel className={classes.compactControlLabel} value="0" control={<Radio color="primary" size="small" />} label={`Location sharing is disabled ${locationSharingScopeLabel}`} />
            <FormControlLabel className={classes.compactControlLabel} value="1" control={<Radio color="primary" size="small" />} label={`Location sharing is enabled, but is not important ${locationSharingScopeLabel}`} />
            <FormControlLabel className={classes.compactControlLabel} value="2" control={<Radio color="primary" size="small" />} label={`Location sharing is important ${locationSharingScopeLabel}`} />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography fontWeight={500}>Group Chat</Typography>
        <FormGroup>
          <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="has_group_chat" checked={!!hasGroupChatValue} onChange={onHasGroupChatChange} />} disabled={!hasWritePerm} label="Enable Group Chat" />
          {/* Per-tour kill switches only -- no operator default exists for either (see TripDefaultSettings2). */}
          {mode === 'trip' && (
            <>
              <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="exclude_from_chat" checked={!!excludeFromChatValue} onChange={onExcludeFromChatChange} />} disabled={!hasWritePerm} label="Disable All Chat Channels" />
              <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="disable_internal_chat" checked={!!disableInternalChatValue} onChange={onDisableInternalChatChange} />} disabled={!hasWritePerm} label="Disable Internal Chat" />
            </>
          )}
        </FormGroup>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel className={classes.compactControlLabel} control={<Checkbox name="mytrips_display" checked={!!myTripsDisplayValue} onChange={onMyTripsDisplayChange} />} disabled={!hasWritePerm} label="Display bookings on the My Trips screen" />
      </Grid>

      {mode === 'trip' && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ marginTop: '1rem', marginBottom: '1rem' }} />
          </Grid>

          <Grid item xs={12}>
            <CheckinAndBoardingSection
              trip={trip}
              hasWritePerm={hasWritePerm}
              onChange={onTripChange}
              clientErrors={clientErrors}
              isParentTouched={isParentTouched}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ marginTop: '1rem', marginBottom: '1rem' }} />
          </Grid>
          
          <Grid item xs={12}>
            <AssociatedTagCategories
              trip={trip}
              hasWritePerm={hasWritePerm}
              onChange={onTripChange}
              clientErrors={clientErrors}
              isParentTouched={isParentTouched}
            />
          </Grid>
        </>
      )}

      {mode === 'operator' && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <CheckinAndBoardingSection
              trip={{ id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] }}
              hasWritePerm={hasWritePerm}
              onChange={(update) => {
                const prev = { id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] };
                const next = typeof update === 'function' ? update(prev) : update;
                onSetDefaultsAssociated && onSetDefaultsAssociated(next.tag_categories);
              }}
              isParentTouched={isParentTouched}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <AssociatedTagCategories
              trip={{ id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] }}
              hasWritePerm={hasWritePerm}
              onChange={(update) => {
                const prev = { id: 'operator-defaults', is_bespoke: false, tag_categories: defaultsAssociatedTagCategories || [] };
                const next = typeof update === 'function' ? update(prev) : update;
                onSetDefaultsAssociated && onSetDefaultsAssociated(next.tag_categories);
              }}
              isParentTouched={isParentTouched}
            />
          </Grid>
        </>
      )}
    </>
  );
};

TripDefaultSettings1.propTypes = {
  mode: PropTypes.oneOf(['operator', 'trip']),
  hasWritePerm: PropTypes.bool,
  hasUpdateTagCategoriesPerm: PropTypes.bool,
  locationSharingValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLocationSharingChange: PropTypes.func,
  myTripsDisplayValue: PropTypes.bool,
  onMyTripsDisplayChange: PropTypes.func,
  hasGroupChatValue: PropTypes.bool,
  onHasGroupChatChange: PropTypes.func,
  excludeFromChatValue: PropTypes.bool,
  onExcludeFromChatChange: PropTypes.func,
  disableInternalChatValue: PropTypes.bool,
  onDisableInternalChatChange: PropTypes.func,

  operatorDefaultCategories: PropTypes.array,
  defaultsAssociatedTagCategories: PropTypes.array,
  onSetDefaultsAssociated: PropTypes.func,

  trip: PropTypes.object,
  onTripChange: PropTypes.func,
  clientErrors: PropTypes.object,
  isParentTouched: PropTypes.any,
};

export default TripDefaultSettings1;


